import { Injectable } from '@angular/core';

/**
 * Contexto do convite (hash, documento e rota de origem).
 *
 * Usa localStorage, nao sessionStorage: o usuario externo nao tem conta e
 * cria uma no ID Goias no meio do fluxo — desvio longo que pode terminar em
 * outra aba (confirmacao por e-mail, pop-up bloqueado, navegador movel).
 * sessionStorage e por aba, entao o contexto se perdia e o retorno caia em
 * "sem permissao para o acesso" com token perfeitamente valido.
 */
@Injectable({ providedIn: 'root' })
export class ConviteStorageService {

    private readonly CHAVE_HASH = 'hashConvite';
    private readonly CHAVE_DOCUMENTO = 'idDocumentos';
    private readonly CHAVE_ROTA = 'rotaInicial';
    private readonly CHAVE_RECUSA = 'motivoRecusaConvite';

    guardar(hashConvite: string | null, documentoId: string | null, rotaInicial: string | null): void {
        if (hashConvite) { localStorage.setItem(this.CHAVE_HASH, hashConvite); }
        if (documentoId) { localStorage.setItem(this.CHAVE_DOCUMENTO, documentoId); }
        if (rotaInicial) { localStorage.setItem(this.CHAVE_ROTA, rotaInicial); }
    }

    obterHashConvite(): string | null {
        return localStorage.getItem(this.CHAVE_HASH);
    }

    obterDocumentoId(): string | null {
        return localStorage.getItem(this.CHAVE_DOCUMENTO);
    }

    obterRotaInicial(): string | null {
        return localStorage.getItem(this.CHAVE_ROTA);
    }

    /**
     * Guarda por que o convite foi recusado para a tela de acesso negado poder explicar.
     *
     * O aceite acontece sozinho no bootstrap, antes de existir tela: sem isto, a recusa por CPF
     * divergente virava um console.error e o usuario so via "ACESSO NEGADO" generico.
     */
    guardarMotivoRecusa(motivo: string): void {
        localStorage.setItem(this.CHAVE_RECUSA, motivo);
    }

    /**
     * Guarda o motivo de uma recusa do aceite, e so dela.
     *
     * Apenas o 403 e o 400 trazem um motivo escrito para o usuario ("convite de outra pessoa",
     * "ja e signatario deste documento"). Falha de
     * banco ou do Portal chega como 500/502 com texto tecnico no detail — exibir isso como
     * razao de acesso negado seria pior do que a tela generica.
     */
    registrarRecusa(erro: any): void {
        const motivo = erro?.error?.detail;
        // ASSINAGO-729: o 400 (IllegalArgumentException) tambem traz mensagem escrita para o usuario
        // ("ja e signatario deste documento"). 500/502 continuam fora.
        if ((erro?.status === 403 || erro?.status === 400) && motivo) {
            this.guardarMotivoRecusa(motivo);
        }
    }

    /** Descarta um motivo que ficou de um bootstrap anterior sem chegar a ser exibido. */
    descartarMotivoRecusa(): void {
        localStorage.removeItem(this.CHAVE_RECUSA);
    }

    /** Le e descarta o motivo — ele vale para uma exibicao so. */
    consumirMotivoRecusa(): string | null {
        const motivo = localStorage.getItem(this.CHAVE_RECUSA);
        localStorage.removeItem(this.CHAVE_RECUSA);
        return motivo;
    }

    limpar(): void {
        localStorage.removeItem(this.CHAVE_HASH);
        localStorage.removeItem(this.CHAVE_DOCUMENTO);
        localStorage.removeItem(this.CHAVE_ROTA);
    }

    /**
     * Remove apenas o documento pendente, apos ser consumido para abrir o
     * modal de visualizacao. Mais preciso que limpar(): nao mexe em
     * hashConvite/rotaInicial, que ja podem ter sido tratados em outro ponto
     * do fluxo.
     */
    limparDocumentoId(): void {
        localStorage.removeItem(this.CHAVE_DOCUMENTO);
    }
}
